import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PlaceOrderDialog } from '@/components/domain/PlaceOrderDialog';
import {
  fetchAllReorderSubmissions,
  productNameOf,
  reorderCandidates,
  type AnySub,
} from '@/lib/ordering';
import { tiersFromSubmission } from '@/lib/prize-tiers';

type PrizePayload = { products: Array<{ id: string; shortName?: string; displayName?: string }> };

// Reorder — ordered + final_approved submissions with real order history and
// live Shopify products. Fetch every cursor page for each status separately so
// newer final_approved rows cannot crowd older ordered rows out;
// reuses PlaceOrderDialog, which shows the reorder banner and opts into
// create-order's duplicate guard via metadata.allowDuplicate.
export default function Reorder() {
  const [q, setQ] = useState('');
  const [orderSub, setOrderSub] = useState<AnySub | null>(null);
  const {
    data: ordered,
    error: orderedError,
    isLoading: orderedLoading,
    mutate: mutateOrdered,
  } = useSWR<AnySub[]>(['reorder-all', 'ordered'], () =>
    fetchAllReorderSubmissions('ordered', get));
  const {
    data: approved,
    error: approvedError,
    isLoading: approvedLoading,
    mutate: mutateApproved,
  } = useSWR<AnySub[]>(['reorder-all', 'final_approved'], () =>
    fetchAllReorderSubmissions('final_approved', get));
  const { data: prize } = useSWR<PrizePayload>('/api/prize-products', get);
  const names = (id?: string) => productNameOf(prize?.products || [], id);
  const isLoading = orderedLoading || approvedLoading;
  const loadError = orderedError || approvedError;
  const refresh = () => Promise.all([mutateOrdered(), mutateApproved()]);

  const list = useMemo(() => {
    const subs = reorderCandidates([
      ...(ordered || []),
      ...(approved || []),
    ]);
    return subs.sort((a, b) => {
      const dateA = a.lastOrderAt || a.firstOrderAt || '';
      const dateB = b.lastOrderAt || b.firstOrderAt || '';
      if (dateA && dateB) return new Date(dateB).getTime() - new Date(dateA).getTime();
      if (dateB) return 1;
      if (dateA) return -1;
      return String(a.fundraiserName || '').localeCompare(String(b.fundraiserName || ''));
    });
  }, [ordered, approved]);

  const filtered = list.filter((s) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(s.fundraiserName || '').toLowerCase().includes(needle) ||
      String(s.id || '').toLowerCase().includes(needle) ||
      String(s.adminName || '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Reorder</h1>
      <p className="mb-3 text-[12px] text-muted">
        Search past orders and reorder individual products · {filtered.length} available
      </p>

      <div className="mb-3 flex items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by fundraiser, admin, or ID…" className="max-w-[300px]" />
        <Button variant="secondary" size="sm" onClick={() => void refresh()}>Refresh</Button>
      </div>

      <div className="rounded-md bg-surface shadow-card">
        {loadError ? (
          <div className="p-8 text-center text-[12px] text-danger">Failed to load submissions.</div>
        ) : isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
        ) : filtered.length ? (
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-subtle">
                <th className="px-3 py-2 font-semibold">Fundraiser</th>
                <th className="px-3 py-2 font-semibold">Admin</th>
                <th className="px-3 py-2 font-semibold">Products</th>
                <th className="px-3 py-2 font-semibold">Last ordered</th>
                <th className="px-3 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const lastOrdered = s.lastOrderAt || s.firstOrderAt;
                return (
                  <tr key={s.id} className="border-b border-border/50 hover:bg-bg/50">
                    <td className="px-3 py-2">
                      <div className="font-medium">{s.fundraiserName || '-'}</div>
                      <div className="font-mono text-[10px] text-subtle">{s.id}</div>
                    </td>
                    <td className="px-3 py-2">{s.adminName || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        {tiersFromSubmission(s).map((t) => (
                          <span key={t.index} className="w-fit rounded-sm bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">
                            {names(t.product || undefined)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[11.5px]">
                      {lastOrdered
                        ? new Date(lastOrdered).toLocaleDateString()
                        : <span className="text-subtle">Not yet ordered</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button variant="secondary" size="sm" onClick={() => setOrderSub(s)}>Reorder</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-[12px] text-muted">
            {q ? 'No matching fundraisers.' : 'No submissions available for reorder — ordered and approved fundraisers appear here.'}
          </div>
        )}
      </div>

      <PlaceOrderDialog
        sub={orderSub}
        open={!!orderSub}
        onClose={() => setOrderSub(null)}
        onPlaced={() => void refresh()}
        productNames={names}
      />
    </div>
  );
}
